import { createAjv } from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { LoadingSpinner, Modal } from "pepsico-ds";
import { useContext, useEffect, useState } from "react";
import { ActivitySetupContext } from "../../../../../context/ActivitySetupContext";
import { CampaignSetupContext } from "../../../../../context/CampaignSetupContext";
import { KeywordGroupProductSelectionContext } from "../../../../../context/KeywordGroupProductSelectionContext";
import { useToast } from "../../../../../hooks/useToast";
import ButtonGroupDynamic from "../../../../common/ButtonGroupDynamic";
import Table from "../../../../common/table/Table";
import ToastNotification from "../../../../common/toastNotification/ToastNotification";
import { ActivitySetupFormWrapper } from "../../../activitySetupFormsWrapper/ActivitySetupFormsWrapper";
import "./products-points-select.scss";

const getHeaderData = () => [
  {
    header: "Product",
    columnKey: "product",
  },
  {
    header: "SKU",
    columnKey: "sku",
  },
];

const getProductsPointsSetTexts = () => ({
  PACKAGE_SCAN: {
    edit: "Points per set",
    toast: "Products and Points per set",
  },
  RECEIPT_SCAN: {
    edit: "Points per $ spent",
    toast: "Products and Points per $ spent",
  },
});

const checkIsRequired = (activity, uischema, coreUiSchema, rulesConfiguration) => {
  const isRequiredConfiguration =
    rulesConfiguration?.rules.find((rule) => rule.rule?.slug === uischema.ruleSlug)
      ?.minRequired > 0;

  return (
    isRequiredConfiguration ||
    activity.activityDefinition.requiredRules?.includes(coreUiSchema.ruleSlug)
  );
};

const ProductsPointsSelectComponent = ({ data, path, uischema, handleChange }) => {
  const {
    loading,
    keywordGroups,
    toggleItem,
    availableItems,
    setSelectedItems,
    handleRemovedData,
    setFilters,
    initialFiltersData,
  } = useContext(KeywordGroupProductSelectionContext);

  useEffect(() => {
    if (data?.ids) {
      setSelectedItems((prev) => {
        const newGloballySelected = [...prev, ...data.ids];
        const uniqueGloballySelected = Array.from(new Set(newGloballySelected));
        return uniqueGloballySelected;
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentStep, setCurrentStep] = useState(0);
  const [edit, setEdit] = useState(false);
  const [currentData, setCurrentData] = useState(data);
  const [showModal, setShowModal] = useState(!data);
  const { currentCampaignTypeConfig } = useContext(CampaignSetupContext);
  const { activity, removeRule, itemsToRemove } = useContext(ActivitySetupContext);
  const [processedItemsToRemove, setProcessedItemsToRemove] = useState(false);
  const { toast, showToast, closeToast } = useToast();
  const toggleModal = () => setShowModal(!showModal);
  const childUiSchemas = uischema.options?.detail?.elements || [];
  const primaryButtonWhileAdding = currentStep === 0 ? "Next" : "Add rule";
  const secondaryButtonWhileAdding = currentStep === 0 ? "Cancel" : "Previous";
  const primaryButtonWhileEditting = "Save";
  const secondaryButtonWhileEditting = "Cancel";
  const ajv = createAjv();

  const {
    core: { schema: coreSchema, uischema: coreUiSchema },
  } = useJsonForms();

  const headerData = getHeaderData();

  const rowData = keywordGroups
    .filter((keywordGroup) => currentData?.ids?.includes(keywordGroup.id))
    .map((keywordGroup) => ({
      product: keywordGroup.name,
      sku: keywordGroup.keywords?.[0] || "-",
    }));

  const activitySlug = activity.activityDefinition.slug;

  const isPackageScan =
    activitySlug === "package-scan-points" ||
    activitySlug === "package-scan-no-unique-code-points";

  const isReceiptScan = activitySlug === "receipt-scan-complete-points";

  const productsPointsSetTexts = getProductsPointsSetTexts();

  const handleActivityTexts = (local) => {
    if (isPackageScan) {
      return productsPointsSetTexts.PACKAGE_SCAN[local];
    }
    if (isReceiptScan) {
      return productsPointsSetTexts.RECEIPT_SCAN[local];
    }
    return "Unknow Activity";
  };

  const onNextStep = () => setCurrentStep(currentStep + 1);
  const onPreviousStep = () => setCurrentStep(currentStep - 1);

  const onCancel = () => {
    if (!currentData) {
      removeRule(coreUiSchema.ruleSlug, coreUiSchema.occurrence);
      toggleModal();
      setEdit(false);
      setFilters(initialFiltersData);
      return;
    }
    handleChange(path, currentData);
    toggleModal();
    setEdit(false);
    setFilters(initialFiltersData);
  };

  const onOpenEdit = (step) => {
    setEdit(true);
    setCurrentStep(step);
    toggleModal();
  };

  // Avoiding infinite loop
  useEffect(() => {
    if (itemsToRemove.length > 0 && !processedItemsToRemove) {
      handleRemovedData(itemsToRemove);
      setProcessedItemsToRemove(true);
    } else if (itemsToRemove.length === 0 && processedItemsToRemove) {
      setProcessedItemsToRemove(false);
    }
  }, [itemsToRemove, handleRemovedData, processedItemsToRemove]);

  const onRemoveSet = () => {
    removeRule(coreUiSchema.ruleSlug, coreUiSchema.occurrence);
  };

  const onSave = () => {
    const hasNoSelectedProducts = Array.isArray(data?.ids) && data.ids.length === 0;

    if (hasNoSelectedProducts) {
      showToast("error", "Please select at least one product id", "");
      return;
    }

    let currentSchema;
    let currentData;

    if (edit) {
      const propertyKey = currentStep === 0 ? "ids" : uischema.pointsKey || "points";

      currentSchema = coreSchema.properties[propertyKey];
      currentData = data[propertyKey];
    } else {
      currentSchema = coreSchema;
      currentData = data;
    }

    const isValid = ajv.validate(currentSchema, currentData);
    const editSuccessfulText =
      currentStep === 0 ? "Products" : handleActivityTexts("edit");
    if (!isValid) {
      showToast("error", "Please fill all the required fields", "");
      return;
    }

    if (Array.isArray(data?.ids)) {
      toggleItem(data.ids);
    }

    setCurrentData(data);
    toggleModal();
    setEdit(false);
    showToast(
      "success",
      `${edit ? editSuccessfulText : handleActivityTexts("toast")} successfully added to activity`,
      ""
    );
    setFilters(initialFiltersData);
  };

  const primaryActionWhileAdding = currentStep === 0 ? onNextStep : onSave;
  const primaryActionWhileEditting = onSave;
  const secondaryActionWhileAdding = currentStep === 0 ? onCancel : onPreviousStep;
  const secondaryActionWhileEditting = onCancel;

  const isOnlyOccurrence =
    activity.activityRules.filter(
      (rule) => rule.ruleDefinition.slug === coreUiSchema.ruleSlug
    ).length === 1;

  const rulesConfiguration =
    activity.activityDefinition.rulesConfiguration ||
    currentCampaignTypeConfig.requirement.possibleActivities.find(
      (a) => a.id === activity.activityDefinition.id
    ).rulesConfiguration;

  const isRequired = checkIsRequired(
    activity,
    uischema,
    coreUiSchema,
    rulesConfiguration
  );

  return (
    <div className="products-points-sets">
      {currentData && (
        <div
          className="products-points-details"
          key={`${coreUiSchema.ruleSlug}-${coreUiSchema.occurrence}`}
        >
          <div className="products-points-header">
            <span className="font-body-bold">
              Participating Products ({coreUiSchema.occurrence})
            </span>
            <ButtonGroupDynamic
              size="small"
              items={[
                {
                  icon: "delete",
                  disabled: isOnlyOccurrence && isRequired,
                  onClick: onRemoveSet,
                },
              ]}
            />
          </div>
          <div className="products-details-container">
            {loading ? (
              <div className="loading-container">
                <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
              </div>
            ) : (
              <>
                <Table
                  headerData={headerData}
                  rowData={rowData}
                  pagination={true}
                  rowsPerPage={10}
                />
                <ButtonGroupDynamic
                  size="small"
                  items={[
                    {
                      icon: "edit",
                      onClick: () => onOpenEdit(0),
                    },
                  ]}
                />
              </>
            )}
          </div>
          <div className="products-details-container align-items-center">
            <div className="product-points-labels">
              <span className="font-xsmall-bold">
                Set {coreUiSchema.occurrence}:{" "}
                {uischema.pointsLabel || "Points per set"}
              </span>
              <span className="font-xsmall-regular">
                {currentData?.[uischema.pointsKey || "points"]}
              </span>
            </div>
            <ButtonGroupDynamic
              size="small"
              items={[
                {
                  icon: "edit",
                  onClick: () => onOpenEdit(1),
                },
              ]}
            />
          </div>
        </div>
      )}
      <Modal
        className="products-rule-setup-modal"
        showModal={showModal}
        onCloseModal={onCancel}
        title={`${edit ? "Edit" : "Add"} rule - ${uischema.title}`}
        showTertiaryButton={false}
        showLink={false}
        secondaryButtonProps={{
          size: "medium",
          text: edit ? secondaryButtonWhileEditting : secondaryButtonWhileAdding,
          variant: "tertiary",
          onClick: edit ? secondaryActionWhileEditting : secondaryActionWhileAdding,
        }}
        primaryButtonProps={{
          size: "medium",
          text: edit ? primaryButtonWhileEditting : primaryButtonWhileAdding,
          variant: "primary",
          disabled: !edit && availableItems.length === 0,
          onClick: edit ? primaryActionWhileEditting : primaryActionWhileAdding,
        }}
      >
        <div className="w-100 col p-0">
          <ActivitySetupFormWrapper
            jsonschema={coreSchema}
            uischema={childUiSchemas[currentStep]}
            data={data}
            onChange={(data) => handleChange(path, data)}
          />
        </div>
        <ToastNotification {...toast} handleClose={closeToast} />
      </Modal>
      {!showModal && <ToastNotification {...toast} handleClose={closeToast} />}
    </div>
  );
};

export const ProductsPointsSelectControl = withJsonFormsControlProps(
  ProductsPointsSelectComponent
);
